import React, {useState, useEffect} from 'react';
import apiInstance from '../../utils/axios';
import UserData from '../plugin/UserData';
import { Link } from 'react-router-dom';
import Slidebar from './Slidebar';
import moment from 'moment';
import { 
    ShoppingBag, 
    Package, 
    Clock, 
    CheckCircle, 
    Eye, 
    FileText, 
    RefreshCw,
    CreditCard,
    MessageCircle,
    AlertCircle,
    Truck,
    XCircle
} from 'lucide-react';


function Orders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const userData = UserData();

    useEffect(() => {
        if (userData?.user_id) {
            setLoading(true);
            setError(null);
            
            apiInstance.get(`customer/orders/${userData.user_id}/`)
                .then((res) => {
                    const ordersData = Array.isArray(res.data) ? res.data : (res.data.results || []);
                    setOrders(ordersData);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching orders:', error);
                    setError("Error al cargar los pedidos. Por favor, inténtalo de nuevo.");
                    setLoading(false);
                });
        } else {
            setLoading(false);
            setError("Usuario no autenticado. Por favor, inicia sesión.");
        }
    }, [userData?.user_id]);

    const statusCounts = Array.isArray(orders) ? orders.reduce((counts, order) => {
        const status = order.order_status;
        counts[status] = (counts[status] || 0) + 1;
        return counts;
    }, {}) : {};

    const getStatusIcon = (status) => {
        switch(status) {
            case 'fulfilled': return <CheckCircle size={16} />;
            case 'pending': return <Clock size={16} />;
            case 'cancelled': return <XCircle size={16} />;
            case 'processing': return <Truck size={16} />;
            default: return <Package size={16} />;
        }
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'fulfilled': case 'paid': return { bg: '#ecfdf5', color: '#10b981' };
            case 'pending': return { bg: '#fffbeb', color: '#f59e0b' };
            case 'cancelled': return { bg: '#fef2f2', color: '#ef4444' };
            case 'processing': return { bg: '#eff6ff', color: '#3b82f6' };
            default: return { bg: '#f1f5f9', color: '#64748b' };
        }
    };

    return (
        <div className="orders-page">
            <div className="orders-container">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner" />
                        <p>Cargando tus pedidos...</p>
                    </div>
                ) : error ? (
                    <div className="error-state">
                        <AlertCircle size={48} />
                        <p>{error}</p>
                        <button onClick={() => window.location.reload()}>
                            <RefreshCw size={18} />
                            Intentar de Nuevo
                        </button>
                    </div>
                ) : (
                    <div className="orders-layout">
                        <aside className="orders-sidebar">
                            <Slidebar />
                        </aside>

                        <main className="orders-main">
                            {/* Stats */}
                            <div className="stats-grid">
                                <div className="stat-card">
                                    <div className="stat-icon total">
                                        <Package size={24} />
                                    </div>
                                    <div className="stat-info">
                                        <span className="stat-value">{orders.length}</span>
                                        <span className="stat-label">Total Pedidos</span>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon pending">
                                        <Clock size={24} />
                                    </div>
                                    <div className="stat-info">
                                        <span className="stat-value">{statusCounts.pending || 0}</span>
                                        <span className="stat-label">Pendientes</span>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon fulfilled">
                                        <CheckCircle size={24} />
                                    </div>
                                    <div className="stat-info">
                                        <span className="stat-value">{statusCounts.fulfilled || 0}</span>
                                        <span className="stat-label">Completados</span>
                                    </div>
                                </div>
                            </div>

                            {/* Orders List */}
                            <div className="orders-card">
                                <div className="card-header">
                                    <h2>
                                        <FileText size={22} />
                                        Historial de Pedidos
                                    </h2>
                                    <button className="refresh-btn" onClick={() => window.location.reload()}>
                                        <RefreshCw size={18} />
                                        Actualizar
                                    </button>
                                </div>

                                {orders.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-icon">
                                            <ShoppingBag size={48} />
                                        </div>
                                        <h3>No tienes pedidos aún</h3>
                                        <p>¡Comienza a comprar para ver tus pedidos aquí!</p>
                                        <Link to="/" className="shop-btn">
                                            <Package size={18} />
                                            Comenzar a Comprar
                                        </Link>
                                    </div>
                                ) : (
                                    <div className="orders-list">
                                        {orders.map((order, index) => {
                                            const orderStatus = getStatusColor(order.order_status);
                                            const paymentStatus = getStatusColor(order.payment_status);
                                            
                                            return (
                                                <div key={index} className="order-item">
                                                    <div className="order-main">
                                                        <div className="order-icon">
                                                            <Package size={24} />
                                                        </div>
                                                        <div className="order-info">
                                                            <h4 className="order-id">Pedido #{order.oid}</h4>
                                                            <span className="order-date">
                                                                {moment(order.date).format('MMM DD, YYYY - h:mm A')}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="order-badges">
                                                        <span 
                                                            className="badge payment-method"
                                                            style={{ 
                                                                backgroundColor: order.payment_method === 'whatsapp' ? '#ecfdf5' : '#eff6ff',
                                                                color: order.payment_method === 'whatsapp' ? '#10b981' : '#3b82f6'
                                                            }}
                                                        >
                                                            {order.payment_method === 'whatsapp' ? (
                                                                <><MessageCircle size={14} /> WhatsApp</>
                                                            ) : (
                                                                <><CreditCard size={14} /> Stripe</>
                                                            )}
                                                        </span>
                                                        <span 
                                                            className="badge payment-status"
                                                            style={{ backgroundColor: paymentStatus.bg, color: paymentStatus.color }}
                                                        >
                                                            {order.payment_status === 'paid' ? 'Pagado' : 
                                                             order.payment_status === 'pending' ? 'Pendiente' : order.payment_status}
                                                        </span>
                                                        <span 
                                                            className="badge order-status"
                                                            style={{ backgroundColor: orderStatus.bg, color: orderStatus.color }}
                                                        >
                                                            {getStatusIcon(order.order_status)}
                                                            {order.order_status === 'fulfilled' ? 'Completado' : 
                                                             order.order_status === 'pending' ? 'Pendiente' : 
                                                             order.order_status === 'cancelled' ? 'Cancelado' : order.order_status}
                                                        </span>
                                                    </div>

                                                    <div className="order-total">
                                                        <span className="total-label">Total</span>
                                                        <span className="total-value">${order.total}</span>
                                                    </div>

                                                    <div className="order-actions">
                                                        <Link to={`/customer/orders/${order.oid}/`} className="action-btn view">
                                                            <Eye size={18} />
                                                            Ver
                                                        </Link>
                                                        <Link to={`/customer/invoice/${order.oid}/`} className="action-btn invoice">
                                                            <FileText size={18} />
                                                            Factura
                                                        </Link>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </main>
                    </div>
                )}
            </div>

            <style>{`
                .orders-page {
                    background: #ffffff;
                    min-height: 100vh;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                }

                /* Layout */
                .orders-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 32px 24px 60px;
                }

                .orders-layout {
                    display: flex;
                    gap: 32px;
                }

                .orders-sidebar {
                    width: 320px;
                    flex-shrink: 0;
                }

                .orders-main {
                    flex: 1;
                    min-width: 0;
                }

                /* Loading & Error */
                .loading-state, .error-state {
                    text-align: center;
                    padding: 80px 20px;
                }

                .spinner {
                    width: 48px;
                    height: 48px;
                    border: 4px solid #e2e8f0;
                    border-top-color: #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 20px;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                .error-state {
                    color: #ef4444;
                }

                .error-state p {
                    color: #64748b;
                    margin: 16px 0;
                }

                .error-state button {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 24px;
                    background: #0f172a;
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-weight: 600;
                    cursor: pointer;
                }

                /* Stats */
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 20px;
                    margin-bottom: 24px;
                }

                .stat-card {
                    background: white;
                    border-radius: 20px;
                    padding: 24px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
                }

                .stat-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                }

                .stat-icon.total { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); }
                .stat-icon.pending { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
                .stat-icon.fulfilled { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }

                .stat-info {
                    display: flex;
                    flex-direction: column;
                }

                .stat-value {
                    font-size: 28px;
                    font-weight: 800;
                    color: #0f172a;
                }

                .stat-label {
                    font-size: 13px;
                    color: #64748b;
                }

                /* Orders Card */
                .orders-card {
                    background: white;
                    border-radius: 24px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
                    overflow: hidden;
                }

                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 24px 28px;
                    border-bottom: 1px solid #f1f5f9;
                }

                .card-header h2 {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-size: 18px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0;
                }

                .refresh-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 16px;
                    background: #f1f5f9;
                    border: none;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .refresh-btn:hover {
                    background: #e2e8f0;
                    color: #0f172a;
                }

                /* Empty State */
                .empty-state {
                    text-align: center;
                    padding: 60px 20px;
                }

                .empty-icon {
                    width: 80px;
                    height: 80px;
                    background: #f1f5f9;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 20px;
                    color: #94a3b8;
                }

                .empty-state h3 {
                    font-size: 18px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0 0 8px;
                }

                .empty-state p {
                    font-size: 14px;
                    color: #64748b;
                    margin: 0 0 24px;
                }

                .shop-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 14px 28px;
                    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    font-size: 15px;
                    font-weight: 600;
                    text-decoration: none;
                    transition: all 0.2s ease;
                }

                .shop-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.2);
                    color: white;
                }

                /* Orders List */
                .orders-list {
                    padding: 12px;
                }

                .order-item {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    padding: 20px;
                    background: #f8fafc;
                    border-radius: 16px;
                    margin-bottom: 12px;
                    transition: all 0.2s ease;
                }

                .order-item:hover {
                    background: #f1f5f9;
                }

                .order-main {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    min-width: 200px;
                }

                .order-icon {
                    width: 48px;
                    height: 48px;
                    background: white;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #3b82f6;
                    flex-shrink: 0;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                }

                .order-info {
                    display: flex;
                    flex-direction: column;
                }

                .order-id {
                    font-size: 15px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0;
                }

                .order-date {
                    font-size: 12px;
                    color: #64748b;
                }

                .order-badges {
                    display: flex;
                    gap: 8px;
                    flex: 1;
                    flex-wrap: wrap;
                }

                .badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    border-radius: 8px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .order-total {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    min-width: 80px;
                }

                .total-label {
                    font-size: 11px;
                    color: #94a3b8;
                    text-transform: uppercase;
                }

                .total-value {
                    font-size: 20px;
                    font-weight: 800;
                    color: #0f172a;
                }

                .order-actions {
                    display: flex;
                    gap: 8px;
                }

                .action-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 10px 16px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    text-decoration: none;
                    transition: all 0.2s ease;
                }

                .action-btn.view {
                    background: #eff6ff;
                    color: #3b82f6;
                }

                .action-btn.view:hover {
                    background: #dbeafe;
                }

                .action-btn.invoice {
                    background: #f1f5f9;
                    color: #64748b;
                }

                .action-btn.invoice:hover {
                    background: #e2e8f0;
                    color: #0f172a;
                }

                /* Responsive */
                @media (max-width: 1024px) {
                    .orders-sidebar {
                        display: none;
                    }

                    .stats-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }

                    .order-item {
                        flex-wrap: wrap;
                    }

                    .order-badges {
                        order: 3;
                        width: 100%;
                        margin-top: 12px;
                    }
                }

                @media (max-width: 768px) {
                    .orders-container {
                        padding: 20px 16px 40px;
                    }

                    .stats-grid {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }

                    .stat-card {
                        padding: 20px;
                    }

                    .order-item {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 16px;
                    }

                    .order-main {
                        width: 100%;
                    }

                    .order-total {
                        flex-direction: row;
                        align-items: center;
                        gap: 8px;
                    }

                    .order-actions {
                        width: 100%;
                    }

                    .action-btn {
                        flex: 1;
                        justify-content: center;
                    }
                }
            `}</style>
        </div>
    );
}

export default Orders;